// ─────────────────────────────────────────────────────────────────────────────
// src/Pages/Payable.jsx  —  DESIGN LAYER
//
// Component : Accounts payable
//
// ┌─ What lives here ────────────────────────────────────────────────────────┐
// │  ✅ JSX render tree                                                      │
// │  ✅ useTheme() for dark/light mode colour tokens                         │
// │  ✅ Layout, spacing, responsive breakpoints                              │
// │  ✅ Conditional rendering                                                │
// │  ❌ NO useState/useEffect/axios — those live in Payable.logic.js          │
// └──────────────────────────────────────────────────────────────────────────┘
//
// Dark-mode pattern used throughout this file:
//   const theme = useTheme();
//   const isDark = theme.palette.mode === "dark";
//   bgcolor: isDark ? "rgba(255,255,255,0.05)" : "background.paper"
//
// Fix visual bugs here. Fix data/API bugs in Payable.logic.js.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import axios from "axios";
import { Box, Grid, Typography, TextField, Select, MenuItem, InputLabel, FormControl, IconButton, InputAdornment, Checkbox, Button, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Autocomplete, CircularProgress, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuth } from '../../context/AuthContext';
import { DataGrid } from "@mui/x-data-grid";
import CurrencySelector from "../../components/CurrencySelector";
import { getCurrentFYear } from '../../utils/financialYear';

export default function Payable({ connectionString }) {
    const { user, selectedCompany, loading } = useAuth();
    // Safely extract values with fallbacks
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const path = connectionString;
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down("sm"));
    const today = dayjs();
    // ─── Lookup state
    const [bookType, setBookType] = useState("");
    const [cashBookTerm, setCashBookTerm] = useState("");
    const [cashBookOptions, setCashBookOptions] = useState([]);
    const [loadingCashBook, setLoadingCashBook] = useState(false);
    const [selectedCashBook, setSelectedCashBook] = useState(null);
    const [partyInput, setPartyInput] = useState("");
    const [partyOptions, setPartyOptions] = useState([]);
    const [selectedParty, setSelectedParty] = useState(null);
    const [loadingParty, setLoadingParty] = useState(false);
    const [isMultiCurrency, setIsMultiCurrency] = useState(false);
    const [currencySearch, setCurrencySearch] = useState("");
    const [currencyResults, setCurrencyResults] = useState([]);
    const [currencyLoading, setCurrencyLoading] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [exchangeRate, setExchangeRate] = useState("");
    const [receiptNo, setReceiptNo] = useState("");
    // ─── Numeric fields
    const [amount, setAmount] = useState("");
    const [tds, setTds] = useState("");
    const [roundOff, setRoundOff] = useState("0");
    const [totalAmt, setTotalAmt] = useState("");
    const [remark, setRemark] = useState("");
    const [receiptDate, setReceiptDate] = useState(dayjs());
    const [bankCharges, setBankCharges] = useState("");
    // Payment type & tables
    const [paymentType, setPaymentType] = useState("BillWise");
    const [invoiceData, setInvoiceData] = useState([]);
    const [selectedBills, setSelectedBills] = useState([]);
    const [transactionNo, setTransactionNo] = useState("");
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    // Error states
    const [errors, setErrors] = useState({
        bookType: false,
        receiptNo: false,
        selectedCashBook: false,
        selectedParty: false,
        amount: false,
        inputAmount: {},
        transactionNo: false,
    });
    // Modal states
    const [open, setOpen] = useState(false);
    const [pendingSelectedIds, setPendingSelectedIds] = useState([]);
    const [fetchedPartyId, setFetchedPartyId] = useState(null);


    console.log("selectedCurrency", selectedCurrency)

    // Reset invoiceData and selectedBills when party changes
    useEffect(() => {
        setInvoiceData([]);
        setSelectedBills([]);
        setFetchedPartyId(null);
    }, [selectedParty?.id]);

    // Helper to reset all errors
    const clearErrors = () => {
        setErrors({
            bookType: false,
            receiptNo: false,
            selectedCashBook: false,
            selectedParty: false,
            amount: false,
            inputAmount: {},
            transactionNo: false,
        });
    };

    // Disable Save if any single inputAmount exceeds the Amount
    const disableSave = selectedBills.some((inv) => {
        const applied = parseFloat(inv.inputAmount) || 0;
        const allowed = parseFloat(amount) || 0;
        return applied > allowed;
    });

    // Debounced cash-book lookup
    useEffect(() => {
        if (!cashBookTerm.trim() || !bookType) {
            setCashBookOptions([]);
            return;
        }
        setLoadingCashBook(true);
        const id = setTimeout(async () => {
            try {
                const res = await axios.get(`${path}/api/booktype-data`, {
                    params: { field: bookType, search: cashBookTerm, companyId },
                });
                setCashBookOptions(res.data.data || []);
            } catch {
                setCashBookOptions([]);
            } finally {
                setLoadingCashBook(false);
            }
        }, 500);
        return () => clearTimeout(id);
    }, [cashBookTerm, bookType]);

    // Receipt number
    const fetchReceiptNo = async (code) => {
        try {
            const { data } = await axios.get(`${path}/api/get-receipt-no`, {
                params: { code, companyId },
            });
            setReceiptNo(data.receiptNo || "");
        } catch {
            setReceiptNo("");
        }
    };

    //fetch all currencies

    const fetchCurrency = async () => {
        try {
            const res = await axios.get(`${path}/currencymaster/search?mode=search&companyId=${companyId}`);

            // IMPORTANT: set data into state
            setCurrencyResults(res.data || []);
        } catch (err) {
            console.error(err);
            setCurrencyResults([]);
        }
    };

    useEffect(() => {
        fetchCurrency()
    }, [])

    const handleBookTypeChange = (e) => {
        const val = e.target.value;
        setBookType(val);
        fetchReceiptNo(val === "Bank Receipt" ? "BR" : "CR");
    };

    // Party lookup
    useEffect(() => {
        setLoadingParty(true);
        const id = setTimeout(async () => {
            try {
                const res = await axios.get(`${path}/api/company-search`, {
                    params: { field: "FirmName", companyId, search: partyInput.trim() || "" },
                });
                const mappedOptions = (res.data.data || []).map((item) => ({
                    id: item.id,
                    clientname: item.FirmName,
                    ContactPerson: item.ContactPerson,
                    City: item.City,
                    MobileNo: item.MobileNo,
                    EmailId: item.EmailId,
                    Address: item.Address,
                    GSTNo: item.GSTNo,
                    state: item.state,
                    Pincode: item.Pincode,
                }));
                setPartyOptions(mappedOptions);
            } catch {
                setPartyOptions([]);
            } finally {
                setLoadingParty(false);
            }
        }, 500);
        return () => clearTimeout(id);
    }, [partyInput]);

    // Currency lookup
    useEffect(() => {
        if (!currencySearch.trim()) {
            setCurrencyResults([]);
            return;
        }
        setCurrencyLoading(true);
        const id = setTimeout(async () => {
            try {
                const res = await axios.get(`${path}/api/currency-search`, {
                    params: { search: currencySearch, companyId },
                });
                setCurrencyResults(res.data.data || []);
            } catch {
                setCurrencyResults([]);
            } finally {
                setCurrencyLoading(false);
            }
        }, 500);
        return () => clearTimeout(id);
    }, [currencySearch]);

    // Recompute overall totalAmt
    useEffect(() => {
        const a = parseFloat(amount) || 0;
        const t = parseFloat(tds) || 0;
        const r = parseFloat(roundOff) || 0;
        setTotalAmt((a + t + r).toFixed(2));
    }, [amount, tds, roundOff]);

    // Clear currency on toggle
    useEffect(() => {
        if (!isMultiCurrency) {
            setCurrencySearch("");
            setCurrencyResults([]);
            setSelectedCurrency(null);
            setExchangeRate("");
        }
    }, [isMultiCurrency]);

    // Handle Save
    const handleSave = async () => {
        if (disableSave) return;
        // Reset previous errors
        clearErrors();

        // Run validation
        let valid = true;
        if (!bookType) {
            setErrors((e) => ({ ...e, bookType: true }));
            valid = false;
        }
        if (!receiptNo) {
            setErrors((e) => ({ ...e, receiptNo: true }));
            valid = false;
        }
        if (!selectedCashBook) {
            setErrors((e) => ({ ...e, selectedCashBook: true }));
            valid = false;
        }
        if (!selectedParty) {
            setErrors((e) => ({ ...e, selectedParty: true }));
            valid = false;
        }
        if (amount.toString().trim() === "") {
            setErrors((e) => ({ ...e, amount: true }));
            valid = false;
        }
        if (transactionNo.toString().trim() === "") {
            setErrors((e) => ({ ...e, transactionNo: true }));
            valid = false;
        }

        // If BillWise, ensure each line has an inputAmount
        if (paymentType === "BillWise") {
            selectedBills.forEach((inv) => {
                if (inv.inputAmount == null || inv.inputAmount.toString().trim() === "") {
                    setErrors((e) => ({ ...e, inputAmount: { ...e.inputAmount, [inv.SaleInvoiceId]: true }, }));
                    valid = false;
                }
            });
        }

        // Stop if any validation failed
        if (!valid) {
            alert("Please fill in all required fields.");
            return;
        }

        // Map each selected bill to billwiseentry
        const billWiseEntries = selectedBills.map((inv, idx) => {
            const amt = parseFloat(inv.inputAmount) || 0;
            const tdsAmt = parseFloat(inv.inputTds) || 0;
            const roundOffAmt = parseFloat(inv.inputRoundOff) || 0;
            const totalPaid = amt + tdsAmt + roundOffAmt;

            return {
                saleInvoiceId: inv.SaleInvoiceId,
                SrNo: idx + 1,
                BillAmt: parseFloat(inv.BasicAmt) || 0,
                OSAMT: (parseFloat(inv.BillAmt) || 0) - amt,
                ChequeAmt: amt,
                TDSAmt: tdsAmt,
                RoundOffAmt: roundOffAmt,
                PaidAmt: totalPaid,
                InvoiceId: inv.SaleInvoiceId,
                Inv_BillNumber: inv.BillNumber,
                Inv_BillDate: inv.BillDate,
                Inv_BillAmt: parseFloat(inv.BillAmt) || 0,
            };
        });

        const payload = {
            L_NO: null,
            PartyId: selectedParty?.id || null,
            BankId: selectedCashBook?.id || null,
            BookName: bookType || null,
            ReceiptNumber: receiptNo || null,
            ReceiptDate: receiptDate?.toISOString() || null,
            ChequeNo: transactionNo || null,
            Amount: amount ? parseFloat(amount) : null,
            BankComm: bankCharges ? parseFloat(bankCharges) : 0,
            TDSAmt: tds ? parseFloat(tds) : null,
            RoundOffAmt: roundOff ? parseFloat(roundOff) : null,
            PaidAmt: totalAmt ? parseFloat(totalAmt) : null,
            VoucherNo: 0,
            VoucherTotal: totalAmt ? parseFloat(totalAmt) : null,
            Remarks: remark || null,
            ClearingAmount: 0,
            ClearingDate: null,
            CurrencyId: selectedCurrency?.currencyId || null,
            ExchangeRate: exchangeRate ? parseFloat(exchangeRate) : null,
            MultiCurrency: isMultiCurrency ? 1 : 0,
            FYear: getCurrentFYear(),
            CompanyId: companyId,
            BranchId: 1,
            AddUserId: userId,
            BillWise: paymentType === "BillWise" ? 1 : 0,
            BillWisePaidAmt: totalAmt ? parseFloat(totalAmt) : null,
            billWiseEntries,
        };

        try {
            const res = await axios.post(`${path}/api/receivable`, payload);
            if (res.data.success) {
                alert(`Saved! New Receipt ID: ${res.data.receiptId}`);
                handleClear();
            } else {
                alert("Save failed");
            }
        } catch (err) {
            console.error(err);
            alert("Error saving receivable");
        }
    };

    const handleClear = () => {
        setBookType("");
        setCashBookTerm("");
        setCashBookOptions([]);
        setSelectedCashBook(null);
        setPartyInput("");
        setPartyOptions([]);
        setSelectedParty(null);
        setIsMultiCurrency(false);
        setCurrencySearch("");
        setCurrencyResults([]);
        setSelectedCurrency(null);
        setExchangeRate("");
        setReceiptNo("");
        setAmount("");
        setTds("");
        setRoundOff("0");
        setTotalAmt("");
        setReceiptDate(today);
        setPaymentType("BillWise");
        setRemark("");
        setInvoiceData([]);
        setSelectedBills([]);
        setBankCharges("");
        setPendingSelectedIds([]);
        setFetchedPartyId(null);
        clearErrors();
    };

    const handleShowTotal = async () => {
        if (!selectedParty?.id) {
            alert("Please select a party first.");
            return;
        }
        if (fetchedPartyId !== selectedParty.id) {
            setLoadingInvoices(true);
            try {
                const res = await axios.post(`${path}/api/fetch-invoice-by-client`, { clientId: selectedParty.id, clientName: selectedParty.clientname, companyId });
                // Add originalIndex to each invoice
                const invoicesWithIndex = (res.data.invoices || []).map((inv, index) => ({
                    ...inv, id: inv.SaleInvoiceId, originalIndex: index,
                }));
                setInvoiceData(invoicesWithIndex);
                setFetchedPartyId(selectedParty.id);
            } catch (err) {
                console.error(err);
                alert("Error fetching invoices.");
            } finally {
                setLoadingInvoices(false);
            }
        }
        setPendingSelectedIds([]);
        setOpen(true);
    };

    const handleDeselectInvoice = (invoice) => {
        setSelectedBills((prev) => prev.filter((i) => i.SaleInvoiceId !== invoice.SaleInvoiceId));
        setInvoiceData((prev) => {
            // Insert invoice at its originalIndex
            const newData = [...prev];
            const index = invoice.originalIndex || prev.length; // Fallback to end if no originalIndex
            newData.splice(index, 0, {
                SaleInvoiceId: invoice.SaleInvoiceId,
                BillNumber: invoice.BillNumber,
                RefNo: invoice.RefNo,
                BillDate: invoice.BillDate,
                BasicAmt: invoice.BasicAmt,
                RoundOff: invoice.RoundOff,
                BillAmt: invoice.BillAmt,
                PaidAmt: invoice.PaidAmt,
                id: invoice.SaleInvoiceId,
                originalIndex: invoice.originalIndex,
            });
            return newData;
        });
    };

    const handleBillFieldChange = (saleInvoiceId, field, value) => {
        setSelectedBills((prev) =>
            prev.map((inv) => {
                if (inv.SaleInvoiceId !== saleInvoiceId) return inv;
                if (field === "inputTotalAmount") return inv;
                return { ...inv, [field]: value };
            })
        );
    };

    const handleSubmitSelected = () => {
        // Get selected invoices based on pendingSelectedIds
        const selectedInvoices = invoiceData.filter((inv) =>
            pendingSelectedIds.includes(inv.SaleInvoiceId)
        );

        // Map selected invoices to include input fields
        const toAdd = selectedInvoices.map((invoice) => ({
            ...invoice,
            inputAmount: invoice.BasicAmt || "",
            inputTds: "",
            inputRoundOff: "",
            inputTotalAmount: invoice.BillAmt || "",
        }));

        // Update selectedBills by adding new selections
        setSelectedBills((prev) => [...prev, ...toAdd]);

        // Remove selected invoices from invoiceData
        setInvoiceData((prev) =>
            prev.filter((inv) => !pendingSelectedIds.includes(inv.SaleInvoiceId))
        );

        // Clear selected IDs and close dialog
        setPendingSelectedIds([]);
        setOpen(false);
    };

    const pendingColumns = [
        {
            field: "select",
            headerName: "",
            width: 80,
            renderCell: (params) => (
                <Checkbox
                    checked={pendingSelectedIds.includes(params.row.SaleInvoiceId)}
                    onChange={(e) => {
                        const id = params.row.SaleInvoiceId;
                        setPendingSelectedIds((prev) =>
                            e.target.checked
                                ? [...prev, id]
                                : prev.filter((selectedId) => selectedId !== id)
                        );
                    }}
                />
            ),
            headerClassName: "data-grid-header",
        },
        {
            field: "BillNumber",
            headerName: "Invoice No",
            width: 180,
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "RefNo",
            headerName: "Reference No",
            width: 180,
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "BillDate",
            headerName: "Date",
            width: 180,
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "BasicAmt",
            headerName: "Basic Amount",
            width: 185,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueGetter: (value, row) => Number(row?.BasicAmt || 0),

            valueFormatter: (value) =>
                !isNaN(value) ? Number(value).toFixed(2) : "0.00",
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "RoundOff",
            headerName: "Round Off",
            width: 180,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueGetter: (value, row) => Number(row?.RoundOff || 0),

            valueFormatter: (value) =>
                !isNaN(value) ? Number(value).toFixed(2) : "0.00",
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "BillAmt",
            headerName: "Grand Total",
            width: 180,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueGetter: (value, row) => Number(row?.BillAmt || 0),

            valueFormatter: (value) =>
                !isNaN(value) ? Number(value).toFixed(2) : "0.00",
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "PaidAmt",
            headerName: "Paid Amt",
            width: 185,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueFormatter: ({ value }) => (value != null ? parseFloat(value).toFixed(2) : "0.00"),
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
    ];

    // DataGrid columns for Selected Bills
    const selectedColumns = [
        {
            field: "select",
            headerName: "",
            width: 50,
            renderCell: (params) => (
                <Checkbox checked onChange={() => handleDeselectInvoice(params.row)} />
            ),
            headerClassName: "data-grid-header",
        },
        {
            field: "BillNumber",
            headerName: "Invoice No",
            width: 130,
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "RefNo",
            headerName: "Reference No",
            width: 130,
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "BillDate",
            headerName: "Date",
            width: 110,
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "BasicAmt",
            headerName: "Basic Amount",
            width: 120,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueGetter: (value, row) => Number(row?.BasicAmt || 0),

            valueFormatter: (value) =>
                !isNaN(value) ? Number(value).toFixed(2) : "0.00",
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "RoundOff",
            headerName: "Round Off",
            width: 90,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueGetter: (value, row) => Number(row?.RoundOff || 0),

            valueFormatter: (value) =>
                !isNaN(value) ? Number(value).toFixed(2) : "0.00",
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "BillAmt",
            headerName: "Grand Total",
            width: 110,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueGetter: (value, row) => Number(row?.BillAmt || 0),

            valueFormatter: (value) =>
                !isNaN(value) ? Number(value).toFixed(2) : "0.00",
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "PaidAmt",
            headerName: "Paid Amt",
            width: 110,
            type: "number",
            headerAlign: "right",
            align: "right",
            valueFormatter: ({ value }) => (value != null ? parseFloat(value).toFixed(2) : "0.00"),
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "inputAmount",
            headerName: "Amount",
            width: 143,
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (
                <TextField
                    size="small"
                    type="text"
                    sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                            borderRadius: "4px",
                            "& input": {
                                textAlign: "right",
                                padding: "4px 8px",
                            },
                        },
                    }}
                    inputProps={{
                        type: "text",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        style: { MozAppearance: "textfield", appearance: "textfield" },
                    }}
                    value={params.row.inputAmount}
                    onChange={(e) => handleBillFieldChange(params.row.SaleInvoiceId, "inputAmount", e.target.value)}
                    error={Boolean(errors.inputAmount[params.row.SaleInvoiceId])}
                />
            ),
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "inputTds",
            headerName: "TDS",
            width: 110,
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (
                <TextField
                    size="small"
                    type="text"
                    sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                            borderRadius: "4px",
                            "& input": {
                                textAlign: "right",
                                padding: "4px 8px",
                            },
                        },
                    }}
                    inputProps={{
                        type: "text",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        style: { MozAppearance: "textfield", appearance: "textfield" },
                    }}
                    value={params.row.inputTds}
                    onChange={(e) => handleBillFieldChange(params.row.SaleInvoiceId, "inputTds", e.target.value)}
                />
            ),
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "inputRoundOff",
            headerName: "RoundOff",
            width: 100,
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (
                <TextField
                    size="small"
                    type="text"
                    sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                            borderRadius: "4px",
                            "& input": {
                                textAlign: "right",
                                padding: "4px 8px",
                            },
                        },
                    }}
                    inputProps={{
                        type: "text",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        style: { MozAppearance: "textfield", appearance: "textfield" },
                    }}
                    value={params.row.inputRoundOff}
                    onChange={(e) => handleBillFieldChange(params.row.SaleInvoiceId, "inputRoundOff", e.target.value)}
                />
            ),
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
        {
            field: "inputTotalAmount",
            headerName: "Total Amount",
            width: 145,
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (
                <TextField
                    size="small"
                    type="text"
                    sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                            borderRadius: "4px",
                            "& input": {
                                textAlign: "right",
                                padding: "4px 8px",
                            },
                        },
                    }}
                    inputProps={{
                        type: "text",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        style: { MozAppearance: "textfield", appearance: "textfield" },
                    }}
                    value={((parseFloat(params.row.inputAmount) || 0) + (parseFloat(params.row.inputTds) || 0) + (parseFloat(params.row.inputRoundOff) || 0)).toFixed(2)}
                    disabled
                />
            ),
            headerClassName: "data-grid-header",
            cellClassName: "data-grid-cell",
        },
    ];

    return (
        <Box sx={{ width: "100%", p: isSm ? 1 : 2, overflow: "auto" }}>
            <Typography
                variant={isSm ? "h5" : "h4"}
                align="center"
                gutterBottom
                sx={{ textDecoration: "underline", mb: isSm ? 1 : 2, fontSize: isSm ? "1.25rem" : "2rem" }}
            >
                Payment
            </Typography>
            <Paper elevation={2} sx={{ p: isSm ? 1 : 2, maxWidth: 1400, mx: "auto", overflow: "hidden" }}>
                <Grid container spacing={isSm ? 1 : 2} justifyContent="center">
                    <Grid item size={{ xs: 12, md: 5 }} sx={{  paddingTop: "5px" }}>
                        <Grid container spacing={isSm ? 1 : 1.5}>
                            {/* Book Name */}



                            <Grid item size={{ xs: 6 }}>
                                <FormControl fullWidth size="small" required error={errors.bookType} sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%" } }}>
                                    <InputLabel>Book Name</InputLabel>
                                    <Select value={bookType} label="Book Name" onChange={handleBookTypeChange}>
                                        <MenuItem value="Bank Receipt">Bank Receipt</MenuItem>
                                        <MenuItem value="Cash Receipt">Cash Receipt</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item size={{ xs:6 }}>
                                <FormControl fullWidth size="small" sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%" } }}>
                                    <InputLabel id="payment-type-label">Payment Type</InputLabel>
                                    <Select
                                        labelId="payment-type-label"
                                        id="payment-type-select"
                                        value={paymentType}
                                        label="Payment Type"
                                        onChange={(e) => setPaymentType(e.target.value)}
                                    >
                                        <MenuItem value="Advance">Advance</MenuItem>
                                        <MenuItem value="BillWise">Bill Wise</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>


                            {/* Date */}
                            <Grid item size={{ xs: 6 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                    <DatePicker
                                        label="Date"
                                        format="DD/MM/YYYY"
                                        value={receiptDate}
                                        onChange={(newVal) => setReceiptDate(newVal)}
                                        slotProps={{ textField: { fullWidth: true, size: "small", sx: { "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%" } } } }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* Receipt No */}
                            <Grid item size={{ xs: 6}}>
                                <TextField
                                    label="Receipt No."
                                    size="small"
                                    fullWidth
                                    value={receiptNo}
                                    InputProps={{ readOnly: true }}
                                    error={errors.receiptNo}
                                    sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%" } }}
                                />
                            </Grid>


                            {/* Cash-Book A/C */}
                            <Grid item size={{ xs: 6 }}>
                                <Autocomplete
                                    freeSolo
                                    options={cashBookOptions}
                                    getOptionLabel={(opt) => opt.accountName || ""}
                                    value={selectedCashBook}
                                    onChange={(_, val) => setSelectedCashBook(val)}
                                    inputValue={cashBookTerm}
                                    onInputChange={(_, v) => setCashBookTerm(v)}
                                    loading={loadingCashBook}
                                    noOptionsText="No records"
                                    openOnFocus
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={<>Cash-Book A/C <span style={{ color: "red" }}>*</span></>}
                                            size="small"
                                            fullWidth
                                            error={errors.selectedCashBook}
                                            sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%" } }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingCashBook && <CircularProgress size={16} />}
                                                        <InputAdornment position="end">
                                                            <IconButton size="small">
                                                                <SearchIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Party Name */}
                            <Grid item size={{ xs: 6 }}>
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    options={partyOptions}
                                    getOptionLabel={(opt) => opt.clientname || ""}
                                    value={selectedParty}
                                    onChange={(_, val) => setSelectedParty(val)}
                                    inputValue={partyInput}
                                    onInputChange={(_, v) => setPartyInput(v)}
                                    loading={loadingParty}
                                    noOptionsText="No records"
                                    openOnFocus
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">{option.clientname}</Typography>
                                                <Typography variant="caption" color="text.secondary">{option.ContactPerson} | {option.City}</Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={<>Party Name <span style={{ color: "red" }}>*</span></>}
                                            size="small"
                                            fullWidth
                                            error={errors.selectedParty}
                                            sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%" } }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingParty && <CircularProgress size={16} />}
                                                        <InputAdornment position="end">
                                                            <IconButton size="small">
                                                                <SearchIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Amount / TDS / RoundOff / Total */}
                            <Grid container item size={{ xs: 12 }} columnSpacing={isSm ? 1 : 1.5} rowSpacing={isSm ? 1 : 1.5}>
                                {/* LEFT COLUMN (5/12 width) */}
                                <Grid item size={{ xs:6}}>
                                    <Grid container direction="column" rowSpacing={isSm ? 1 : 1.5}>
                                        <Grid item>
                                            <TextField
                                                label={<>Check No. <span style={{ color: "red" }}>*</span></>}
                                                type="number"
                                                size="small"
                                                fullWidth
                                                value={transactionNo}
                                                onChange={(e) => setTransactionNo(e.target.value)}
                                                error={errors.transactionNo}
                                                sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%", fontSize: "0.875rem" } }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                label={<>Amount <span style={{ color: "red" }}>*</span></>}
                                                type="number"
                                                size="small"
                                                fullWidth
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                error={errors.amount}
                                                sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%", fontSize: "0.875rem" } }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                label="TDS"
                                                type="number"
                                                size="small"
                                                fullWidth
                                                value={tds}
                                                onChange={(e) => setTds(e.target.value)}
                                                sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%", fontSize: "0.875rem" } }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                label="Round Off"
                                                type="number"
                                                size="small"
                                                fullWidth
                                                value={roundOff}
                                                onChange={(e) => setRoundOff(e.target.value)}
                                                sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%", fontSize: "0.875rem" } }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                label="Total Amt"
                                                size="small"
                                                fullWidth
                                                value={totalAmt}
                                                disabled
                                                sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%", fontSize: "0.875rem" } }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* RIGHT COLUMN (7/12 width) */}
                                <Grid item size={{ xs: 6 }}>
                                    <Grid container direction="column" rowSpacing={isSm ? 1 : 1.5}>
                                        <Grid item size={{ xs:6}}>
                                            <CurrencySelector
                                                currencies={currencyResults}
                                                value={selectedCurrency}
                                                onChange={setSelectedCurrency}
                                                exchangeRate={exchangeRate}
                                                onExchangeRateChange={setExchangeRate}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                label="Bank Chrg."
                                                size="small"
                                                fullWidth
                                                value={bankCharges}
                                                onChange={(e) => setBankCharges(e.target.value)}
                                                sx={{ "& .MuiInputBase-root": { height: "2.5rem", maxWidth: "100%", fontSize: "0.875rem" } }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                label="Remark"
                                                multiline
                                                minRows={3.5}
                                                fullWidth
                                                size="small"
                                                value={remark}
                                                onChange={(e) => setRemark(e.target.value)}
                                                sx={{
                                                    "& .MuiInputBase-root:not(.MuiInputBase-multiline)": { height: "2.5rem", maxWidth: "100%", fontSize: "0.875rem" },
                                                    "& .MuiOutlinedInput-inputMultiline": { padding: "0.5rem 0.75rem", minHeight: "5rem", fontSize: "0.875rem" },
                                                    maxWidth: "100%",
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* BillWise Button */}
                            {paymentType === "BillWise" && (
                                <Grid item size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            size="small"
                                            onClick={handleShowTotal}
                                            sx={{ fontSize: "0.875rem", padding: "0.5rem" }}
                                        >
                                            Show Outstanding Entries
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    {/* BillWise DataGrid */}
                    {paymentType === "BillWise" && (
                        <Grid item size={{ xs: 12 }}>
                            <Paper variant="outlined" sx={{ p: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.1)", width: "100%" }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ fontSize: isSm ? "0.875rem" : "1rem" }}>
                                    Selected Bills
                                </Typography>
                                <DataGrid
                                    rows={selectedBills}
                                    columns={selectedColumns}
                                    getRowId={(row) => row.SaleInvoiceId}
                                    autoHeight
                                    disableSelectionOnClick
                                    disableColumnResize
                                    hideFooter
                                    sx={{
                                        width: "100%",
                                        "& .data-grid-header": {
                                            backgroundColor: '#f0f0f0',
                                            fontWeight: "bold",
                                            fontSize: isSm ? "0.75rem" : "0.875rem",
                                            borderBottom: "2px solid #d0d4d9",
                                            padding: "0.5rem",
                                            color: "#1a3c6c",
                                        },
                                        "& .data-grid-cell": {
                                            fontSize: isSm ? "0.75rem" : "0.875rem",
                                            whiteSpace: "normal",
                                            overflowWrap: "break-word",
                                            padding: "0.5rem",
                                            borderBottom: "1px solid #e8ecef",
                                        },
                                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                                            background: "linear-gradient(90deg, #f9fafb 0%, #e8f0fe 100%)",
                                        },
                                        "& .MuiDataGrid-row:nth-of-type(even)": {
                                            background: "linear-gradient(90deg, #ffffff 0%, #f0f4ff 100%)",
                                        },
                                        "& .MuiDataGrid-row:hover": {
                                            background: "linear-gradient(90deg, #e3f2fd 0%, #bbdefb 100%)",
                                            transition: "background 0.3s ease-in-out",
                                        },
                                        "& .MuiDataGrid-row": {
                                            border: "none",
                                            borderRadius: "4px",
                                            margin: "2px 0",
                                        },
                                        border: "1px solid #d0d4d9",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                    }}
                                    rowHeight={isSm ? 48 : 52}
                                />
                                {selectedBills.length === 0 && (
                                    <Typography sx={{ textAlign: "center", fontSize: isSm ? "0.75rem" : "0.875rem", py: 1, color: "text.secondary" }}>
                                        No Bills Selected…
                                    </Typography>
                                )}
                                {selectedBills.length > 0 && (
                                    <Box sx={{ pt: 1, borderTop: "1px solid #d0d4d9", textAlign: "right" }}>
                                        <Typography sx={{ fontWeight: "bold", fontSize: isSm ? "0.75rem" : "0.875rem", color: "#1a3c6c" }}>
                                            Applied Amt: {selectedBills.reduce((sum, i) => sum + (parseFloat(i.inputAmount) || 0), 0).toFixed(2)} &nbsp;
                                            Applied TDS: {selectedBills.reduce((sum, i) => sum + (parseFloat(i.inputTds) || 0), 0).toFixed(2)} &nbsp;
                                            Applied RoundOff: {selectedBills.reduce((sum, i) => sum + (parseFloat(i.inputRoundOff) || 0), 0).toFixed(2)} &nbsp;
                                            Applied Total: {selectedBills.reduce((sum, i) => sum + ((parseFloat(i.inputAmount) || 0) + (parseFloat(i.inputTds) || 0) + (parseFloat(i.inputRoundOff) || 0)), 0).toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {/* Footer Buttons */}
                <Box sx={{ textAlign: "center", mt: 3 }}>
                    <Button variant="contained" size="small" sx={{ mr: 2, fontSize: "0.875rem", padding: "0.5rem" }} onClick={handleSave} disabled={disableSave}>
                        Save
                    </Button>
                    <Button variant="outlined" size="small" sx={{ fontSize: "0.875rem", padding: "0.5rem" }} onClick={handleClear}>
                        Cancel
                    </Button>
                </Box>
            </Paper>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" sx={{ "& .MuiDialog-paper": { width: "80%", maxWidth: "1400px" } }}>
                <DialogTitle sx={{ fontSize: isSm ? "0.875rem" : "1rem" }}>Pending Invoices</DialogTitle>
                <DialogContent>
                    <DataGrid
                        rows={invoiceData}
                        columns={pendingColumns}
                        getRowId={(row) => row.SaleInvoiceId}
                        autoHeight
                        disableColumnResize
                        loading={loadingInvoices}
                        hideFooter
                        disableRowSelectionOnClick
                        sx={{
                            width: "100%",
                            "& .data-grid-header": {
                                backgroundColor: '#f0f0f0',
                                fontWeight: "bold",
                                fontSize: isSm ? "0.75rem" : "0.875rem",
                                borderBottom: "2px solid #d0d4d9",
                                padding: "0.5rem",
                                color: "#1a3c6c",
                            },
                            "& .data-grid-cell": {
                                fontSize: isSm ? "0.75rem" : "0.875rem",
                                whiteSpace: "normal",
                                overflowWrap: "break-word",
                                padding: "0.5rem",
                                borderBottom: "1px solid #e8ecef",
                            },
                            "& .MuiDataGrid-row:nth-of-type(odd)": {
                                backgroundColor: "#f9fafb",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#e8f0fe",
                                transition: "background-color 0.2s ease-in-out",
                            },
                            "& .MuiDataGrid-row": {
                                border: "none",
                            },
                            border: "1px solid #d0d4d9",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                        rowHeight={isSm ? 48 : 52}
                    />
                    {invoiceData.length === 0 && !loadingInvoices && (
                        <Typography sx={{ textAlign: "center", fontSize: isSm ? "0.75rem" : "0.875rem", py: 1, color: "text.secondary" }}>
                            No Record Found…
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} sx={{ fontSize: "0.875rem", padding: "0.5rem" }}>Cancel</Button>
                    <Button onClick={handleSubmitSelected} disabled={pendingSelectedIds.length === 0} sx={{ fontSize: "0.875rem", padding: "0.5rem" }}>Submit</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}